import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import userService from '../api/profile';
import { clearTokens } from '../api/refreshToken';
import OverlayLoader from '../../components/overlayLoader';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const navigate = useNavigate();
    const navigateRef = useRef(navigate);
    navigateRef.current = navigate;
    const location = useLocation();

    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [accessToken, setAccessToken] = useState(
        localStorage.getItem('access_token') || null
    );
    const [errorCode, setErrorCode] = useState(null);  // Add state to handle error codes
    const [user, setUser] = useState(null);
    const [userDataLoading, setUserDataLoading] = useState(!!accessToken);

    const fetchUserData = useCallback(() => {
        if (!accessToken) {
            return;
        }

        setUserDataLoading(true);
        userService.getUserProfile(setIsAuthenticated, navigateRef.current, false)
            .then((user) => {
                setUser(user);
                setIsAuthenticated(!!user);
            })
            .catch((error) => {
                console.log(error);
                setAccessToken(null);
            })
            .finally(() => {
                setUserDataLoading(false);
            });
    }, [accessToken, setIsAuthenticated]);

    const login = (token, refreshToken) => {
        localStorage.setItem('access_token', token);
        if (refreshToken) {
            localStorage.setItem('refresh_token', refreshToken);
        }
        setAccessToken(token);
    };

    const logout = () => {
        setIsAuthenticated(false);
        setUser(null);
        setAccessToken(null);
        clearTokens(); // Clear both tokens on logout
    };

    useEffect(() => {
        fetchUserData();
    }, [fetchUserData]);

    // Function to handle API errors and set the error code
    const handleApiError = (error) => {
        if (error.response) {
            setErrorCode(error.response.status);  // Set error code like 401, 403, 500, etc.
        } else {
            setErrorCode(500);  // Default to 500 if no response from server
        }
    };

    if (userDataLoading) {
        return (
            <OverlayLoader
                open
                text="Loading user data..."
            />
        );
    }

    const signInRoutes = ['/', '/sign-in', '/partner-sign-in'];
    if (isAuthenticated && signInRoutes.includes(location.pathname)) {
        return <Navigate to={'/dashboard'} replace />;
    }

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                errorCode,
                user,
                setIsAuthenticated,
                login,
                logout,
                handleApiError,
                refreshUserData: fetchUserData,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
